import dayjs from 'dayjs';
import svLocale from 'dayjs/locale/sv';
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import 'dayjs/locale/nl';
import 'dayjs/locale/nb';
import 'dayjs/locale/da';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import durationPlugin from 'dayjs/plugin/duration';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import isYesterday from '@/mixins/dayjs/isYesterday';
import isToday from '@/mixins/dayjs/isToday';
import isTomorrow from '@/mixins/dayjs/isTomorrow';
import isStartOfDay from '@/mixins/dayjs/isStartOfDay';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import minMax from 'dayjs/plugin/minMax';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBankHoliday from '@/mixins/dayjs/isBankHoliday';
import weeksInMonth from '@/mixins/dayjs/weeksInMonth';
import isCurrentYear from '@/mixins/dayjs/isCurrentYear';
import isWeekend from '@/mixins/dayjs/isWeekend';
import isHoliday from '@/mixins/dayjs/isHoliday';
import toHumanReadableDate from '@/mixins/dayjs/toHumanReadableDate';
import formatDateForServer from '@/mixins/dayjs/formatDateForServer';
import formatDateTimeForServer from '@/mixins/dayjs/formatDateTimeForServer';
/**
 * Standard timezone for the application
 *
 * When CampingOnline gets multi timezone support, this will
 * need to come from the server and be set per campground.
 */
export const APP_TIMEZONE = 'Europe/Stockholm';
/**
 * The user's browser timezone
 */
export const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
/* Day.js and plugins */
dayjs.locale(svLocale);
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(durationPlugin);
dayjs.extend(relativeTimePlugin);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(minMax);
dayjs.extend(isLeapYear);
dayjs.extend(customParseFormat);
// Egna plugins nedan
dayjs.extend(isToday); // den officiella pluginen använder alltid lokal tidszon
dayjs.extend(isTomorrow); // den officiella pluginen använder alltid lokal tidszon
dayjs.extend(isYesterday); // den officiella pluginen använder alltid lokal tidszon
dayjs.extend(isStartOfDay);
dayjs.extend(isBankHoliday);
dayjs.extend(weeksInMonth);
dayjs.extend(isCurrentYear);
dayjs.extend(isWeekend);
dayjs.extend(isHoliday);
dayjs.extend(toHumanReadableDate);
dayjs.extend(formatDateForServer);
dayjs.extend(formatDateTimeForServer);
/**
 * Get current date/time in Stockholm timezone
 */
export const now = () => dayjs().tz(APP_TIMEZONE);
/**
 * Get today's date (start of day) in Stockholm timezone
 */
export const today = () => dayjs().tz(APP_TIMEZONE).startOf('day');
/**
 * Get tomorrow's date (start of day) in Stockholm timezone
 */
export const tomorrow = () => dayjs().tz(APP_TIMEZONE).add(1, 'days').startOf('day');
/**
 * Get yesterday's date (start of day) in Stockholm timezone
 */
export const yesterday = () => dayjs().tz(APP_TIMEZONE).subtract(1, 'days').startOf('day');
/**
 * Get the timezone identifier of a date or undefined if not set
 */
export const getTimeZone = (date) => {
    if ('$x' in date &&
        typeof date.$x === 'object' &&
        date.$x !== null &&
        '$timezone' in date.$x &&
        typeof date.$x.$timezone === 'string') {
        return date.$x.$timezone;
    }
    return undefined;
};
/**
 * Parse a date string from user input (date picker, form field)
 * Assumes the date is already in Stockholm timezone context.
 * Sets timezone to Stockholm WITHOUT converting the time.
 *
 * @example
 * // User selects "2024-01-15" in date picker
 * parseDate('2024-01-15') // Returns 2024-01-15 00:00:00 in Stockholm
 */
export const parseDate = (val) => {
    if (!val) {
        return null;
    }
    return dayjs.tz(val, APP_TIMEZONE).startOf('day');
};
/**
 * Parse a date-time string from user input
 * Assumes the date-time is already in Stockholm timezone context.
 * Sets timezone to Stockholm WITHOUT converting the time.
 *
 * @example
 * // User selects date and time in form
 * parseDateTime('2024-01-15 14:30') // Returns 2024-01-15 14:30:00 in Stockholm
 */
export const parseDateTime = (val) => {
    if (!val) {
        return null;
    }
    return dayjs.tz(val, APP_TIMEZONE);
};
/**
 * Parse a date from server response
 * (assumes UTC/ISO format)
 * Converts from UTC to Stockholm timezone.
 *
 * @example
 * parseServerDate('2024-01-15T22:00:00Z') // Returns 2024-01-16 00:00:00 in Stockholm
 * parseServerDate('2024-01-16') // Returns 2024-01-16 00:00:00 in Stockholm
 */
export const parseServerDate = (val) => {
    if (!val) {
        return null;
    }
    return dayjs.tz(val, 'UTC').tz(APP_TIMEZONE).startOf('day');
};
/**
 * Parse a date-time from server response
 * (assumes UTC/ISO format)
 * Converts from UTC to Stockholm timezone.
 *
 * @example
 * // Server returns "2024-01-15T14:30:00Z"
 * parseServerDateTime('2024-01-15T14:30:00Z') // Returns 2024-01-15 14:30:00 in Stockholm
 */
export const parseServerDateTime = (val) => {
    if (!val) {
        return null;
    }
    if (typeof val === 'string' && val.length === 10) {
        // Vissa server datum skickas nu som "YYYY-MM-DD"
        return dayjs.tz(val, APP_TIMEZONE);
    }
    return dayjs.tz(val, 'UTC').tz(APP_TIMEZONE);
};
/**
 * Format a duration into a human-readable Swedish string with exact values.
 * Unlike dayjs .humanize(), this doesn't round (90 min → "1 timme 30 minuter", not "2 timmar").
 */
export function formatDuration(duration, opts) {
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const parts = [];
    if (days > 0) {
        switch (opts?.style ?? 'normal') {
            case 'normal':
                parts.push(`${days} ${days === 1 ? 'dag' : 'dagar'}`);
                break;
            case 'short':
                parts.push(`${days} d`);
                break;
        }
    }
    if (hours > 0) {
        switch (opts?.style ?? 'normal') {
            case 'normal':
                parts.push(`${hours} ${hours === 1 ? 'timme' : 'timmar'}`);
                break;
            case 'short':
                parts.push(`${hours} h`);
                break;
        }
    }
    if (minutes > 0) {
        switch (opts?.style ?? 'normal') {
            case 'normal':
                parts.push(`${minutes} ${minutes === 1 ? 'minut' : 'minuter'}`);
                break;
            case 'short':
                parts.push(`${minutes} m`);
                break;
        }
    }
    if (opts?.singleUnit) {
        return parts[0] || (opts?.style === 'short' ? '0' : '0 min');
    }
    return parts.join(', ') || (opts?.style === 'short' ? '0' : '0 min');
}
Object.assign(dayjs, {
    now,
    today,
    tomorrow,
    yesterday,
    parseDate,
    getTimeZone,
    parseDateTime,
    parseServerDate,
    parseServerDateTime
});
window.dayjs = dayjs;
export { dayjs };
